/**
 * Blog Post Detail — Route Module
 *
 * Replaces app/[locale]/(marketing)/blog/[slug]/page.tsx.
 * Fetches full post content from BlogService with SEO metadata from backend.
 */
import { PageContainer } from '@letsgethome/ui/page-container';
import { useLoaderData } from 'react-router';

import { Breadcrumbs } from '../../../../shared/components/Breadcrumbs';
import { SeoHead } from '../../../../shared/components/SeoHead';
import { useLocale, useTranslations } from '../../../../shared/i18n';
import { PostArticle, preparePostArticle } from '../components/PostArticle';
import { PostCard } from '../components/PostCard';
import { TableOfContents } from '../components/TableOfContents';
import type { loader } from './blog-post.loader';

export function Component() {
  const { post, relatedPosts, seo } = useLoaderData<typeof loader>();
  const locale = useLocale();
  const t = useTranslations('blog');

  const { headings } = preparePostArticle(post, locale);

  const translations = {
    listenToArticle: t('listenToArticle'),
    byAuthor: (author: string) => t('byAuthor', { author }),
    tableOfContents: t('tableOfContents'),
    partnerRoleFallback: t('partnerRoleFallback'),
  };

  return (
    <>
      <SeoHead {...seo} />
      <div className="min-h-screen bg-background">
        <PageContainer size="7xl" className="py-12 sm:px-6 lg:px-8">
          <Breadcrumbs items={seo.breadcrumbs} />
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-8">
            <aside className="hidden xl:block xl:col-span-3">
              <div className="sticky top-24">
                <TableOfContents headings={headings} title={t('tableOfContents')} />
              </div>
            </aside>

            <div className="xl:col-span-9">
              <PostArticle
                post={post}
                locale={locale}
                translations={translations}
                isPreview={false}
                showMobileToc={true}
              />
            </div>
          </div>
        </PageContainer>

        {relatedPosts.length > 0 && (
          <section className="bg-background py-12">
            <PageContainer size="7xl" className="sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold mb-8">{t('relatedPosts')}</h2>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {relatedPosts.map((related) => (
                  <PostCard
                    key={related.id}
                    post={related}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  />
                ))}
              </div>
            </PageContainer>
          </section>
        )}
      </div>
    </>
  );
}
